import { AnyAction, ThunkDispatch, createSlice } from '@reduxjs/toolkit';
import { Dispatch } from 'react';
import { useDispatch } from 'react-redux';

import { SystemNotification } from '../models/notifications';
import {
    clearCache,
    deleteNotification,
    getInternalSettings,
    getSystemNotifications,
    postNotification,
    saveInternalSettings,
    updateNotification,
} from './actions';

export interface AppSettingsReducerState {
    lobsterXmlOrderUrl: string;
    defaultCfpTextContract: string;
    defaultCfpTextBonus: string;
    statusScale: Record<string, string> | null;
    requestingSystemCache: boolean;
    requestingLobsterXml: boolean;
    notifications: SystemNotification[];
}

const initialState: AppSettingsReducerState = {
    lobsterXmlOrderUrl: '',
    defaultCfpTextContract: '',
    defaultCfpTextBonus: '',
    statusScale: null,
    requestingSystemCache: false,
    requestingLobsterXml: false,
    notifications: [],
};

const appSettingsSlice = createSlice({
    name: 'app-settings',
    initialState,
    reducers: {},
    extraReducers: builder => {
        builder.addCase(clearCache.pending, state => {
            state.requestingSystemCache = true;
        });
        builder.addCase(clearCache.fulfilled, state => {
            state.requestingSystemCache = false;
        });
        builder.addCase(saveInternalSettings.pending, state => {
            state.requestingLobsterXml = true;
        });
        builder.addCase(saveInternalSettings.fulfilled, state => {
            state.requestingLobsterXml = false;
        });
        builder.addCase(getInternalSettings.fulfilled, (state, action) => {
            state.lobsterXmlOrderUrl = action.payload?.lobsterXmlOrderUrl ?? '';
            state.defaultCfpTextContract = action.payload?.defaultCfpTextContract ?? '';
            state.defaultCfpTextBonus = action.payload?.defaultCfpTextBonus ?? '';
            state.statusScale = action.payload?.statusScale ?? null;
        });
        builder.addCase(getSystemNotifications.fulfilled, (state, action) => {
            state.notifications = action.payload ?? ([] as SystemNotification[]);
        });
        builder.addCase(postNotification.fulfilled, (state, action) => {
            if (action.payload.success) {
                state.notifications.push(action.payload.result);
            }
        });
        builder.addCase(updateNotification.fulfilled, (state, action) => {
            if (action?.payload?.success) {
                state.notifications = state.notifications.map(notification =>
                    notification.id === action.payload?.result.id ? action.payload?.result : notification
                );
            }
        });
        builder.addCase(deleteNotification.fulfilled, (state, action) => {
            state.notifications = state.notifications.filter(notification => notification.id !== action.payload);
        });
    },
});

export type AppDispatch = ThunkDispatch<AppSettingsReducerState, undefined, AnyAction> & Dispatch<AnyAction>;
export const useAppDispatch: () => AppDispatch = useDispatch;
export const { reducer, name } = appSettingsSlice;
export type AppSettingsSlice = { [name]: AppSettingsReducerState };
